<template>
  <div class="ts-flex ts-items-center ts-justify-center" style="width: 100%;">
    <div
      class="ts-bg-white ts-rounded-xl ts-shadow-md ts-w-full ts-max-w-4xl ts-overflow-hidden ts-border ts-border-gray-200 ts-cursor-pointer ts-transition-transform hover:ts-scale-105"
      @click="showOverlay = true">
      <div class="ts-flex ts-flex-col md:ts-flex-row">
        <!-- Left Side - Header -->
        <div class="ts-bg-gray-700 ts-text-white ts-p-2 md:ts-w-80 ts-flex ts-flex-col ts-justify-center">
          <h2 class="ts-text-xl ts-font-semibold ts-mb-1 ts-text-white">{{ $t('restaurantSale.invoiceNo') }}</h2>
          <div class="ts-text-sm ts-opacity-90 ts-font-medium ts-tracking-wide ts-mb-1">
            {{ invoice.invoice_no }}
          </div>

          <!-- Grand Total in Sidebar -->
          <div class="ts-border-t ts-border-gray-600 ts-pt-3 ts-mt-2">
            <div class="ts-text-sm ts-opacity-75 ts-mb-1">{{ $t('restaurantSale.grandTotal') }} </div>
            <div class="ts-text-sm ts-font-bold">
              ${{ formatAmount(invoice.grand_total) }}
            </div>
          </div>
        </div>

        <!-- Right Side - Details -->
        <div class="ts-flex-1 ts-p-2">
          <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 ts-gap-3">
            <!-- Invoice Date -->
            <div class="ts-flex ts-flex-col">
              <div
                class="ts-flex ts-items-center ts-gap-2 ts-text-sm ts-text-gray-500 ts-font-medium ts-mb-2 ts-whitespace-no-wrap">
                <svg class="ts-w-4 ts-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {{ $t('restaurantSale.invoiceDate') }}
              </div>
              <div class="ts-text-sm ts-text-gray-900 ts-font-semibold ts-whitespace-no-wrap">
                {{ invoice.stock_out_date }}
              </div>
            </div>

            <!-- Locker -->
            <div class="ts-flex ts-flex-col">
              <div class="ts-flex ts-items-center ts-gap-2 ts-text-sm ts-text-gray-500 ts-font-medium ts-mb-2">
                <svg class="ts-w-4 ts-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="7" width="18" height="13" rx="2" />
                  <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                {{ $t('restaurantSale.tableNo') }}
              </div>
              <div class="ts-text-sm ts-text-gray-900 ts-font-semibold">
                {{ invoice.table_no ? invoice.table_no : 'N/A' }}
              </div>
            </div>

            <!-- Payment Method -->
            <div class="ts-flex ts-flex-col">
              <div
                class="ts-flex ts-items-center ts-gap-2 ts-text-sm ts-text-gray-500 ts-font-medium ts-mb-2 ts-whitespace-no-wrap">
                <svg class="ts-w-4 ts-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
                {{ $t('restaurantSale.paymentMethod') }}
              </div>
              <div>
                <span
                  class="ts-px-4 ts-py-1 ts-rounded-full ts-text-xs ts-font-semibold ts-uppercase ts-tracking-wide ts-text-white ts-inline-block"
                  :class="getPaymentBadgeClass(invoice.payment_method)">
                  {{ invoice.payment_method }}
                </span>
              </div>
            </div>

            <!-- Cashier -->
            <div class="ts-flex ts-flex-col">
              <div class="ts-flex ts-items-center ts-gap-2 ts-text-sm ts-text-gray-500 ts-font-medium ts-mb-2">
                <svg class="ts-w-4 ts-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {{ $t('restaurantSale.cashier') }}
              </div>
              <div class="ts-text-sm ts-text-gray-900 ts-font-semibold">
                {{ invoice.cashier_name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay Modal -->
    <div v-if="showOverlay"
      class="ts-fixed ts-inset-0 ts-bg-black ts-bg-opacity-50 ts-flex ts-items-center ts-justify-center ts-z-50"
      @click="showOverlay = false">
      <div class="ts-bg-white ts-rounded-lg ts-p-8 ts-max-w-md ts-w-full ts-mx-4" @click.stop>
        <div class="ts-text-center">
          <div class="ts-mb-6">
            <svg class="ts-w-16 ts-h-16 ts-mx-auto ts-text-gray-700" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
          </div>

          <h3 class="ts-text-2xl ts-font-semibold ts-text-gray-900 ts-mb-2">Re-print Invoice</h3>
          <p class="ts-text-gray-600 ts-mb-6">{{ invoice.invoice_no }}</p>

          <div class="ts-flex ts-gap-3">
            <button @click="showOverlay = false"
              class="ts-flex-1 ts-px-6 ts-py-3 ts-bg-gray-200 ts-text-gray-700 ts-rounded-lg ts-font-medium hover:ts-bg-gray-300 ts-transition-colors">
              Cancel
            </button>
            <button @click="handleReprint"
              class="ts-flex-1 ts-px-6 ts-py-3 ts-bg-gray-700 ts-text-white ts-rounded-lg ts-font-medium hover:ts-bg-gray-800 ts-transition-colors">
              Re-print
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvoiceCard',
  props: {
    invoice: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      showOverlay: false
    }
  },
  methods: {
    getPaymentBadgeClass(method) {
      const methodLower = method ? method.toLowerCase() : '';
      if (methodLower === 'cash') {
        return 'ts-bg-green-600';
      } else if (methodLower === 'card') {
        return 'ts-bg-blue-500';
      } else if (methodLower === 'transfer') {
        return 'ts-bg-purple-600';
      }
      return 'ts-bg-gray-600';
    },
    formatAmount(amount) {
      return parseFloat(amount).toFixed(2);
    },
    handleReprint() {
      // Emit event to parent component
      this.$emit('reprint', this.invoice);
      this.showOverlay = false;
    }
  }
}
</script>

<style scoped>
/* Optional: Add any custom styles if needed */
</style>